import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { AccessorKeyColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Route, Routes } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { getCustomers } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { Customer } from '../../types';
import AnalyticsHeader from '../../widgets/Header';
import CreateCustomerModal from './CreateCustomerModal';
import CustomerDetail from './ViewAndEditDetail/ViewAndEditDetail';

const CreateCustomersButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> New Customer </Button>;
};

export const GET_CUSTOMERS_KEY = ['customers'];

const columnHelper = createColumnHelper<Customer>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('phoneNumber', {
    header: 'Phone',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('customerType', {
    header: 'Customer Type',
    cell: info => info.getValue(),
    footer: info => info.column.id
  })
];

export interface CustomerTableProps {
  query: Record<string, unknown>;
}

const CustomersTable = ({ query }: CustomerTableProps) => {
  const compositeQueryKey = [...GET_CUSTOMERS_KEY, query];
  const { data, isLoading } = useQuery({ queryKey: compositeQueryKey, queryFn: () => getCustomers(query) });
  const customers = data?.results;
  const [showCreateModal, setShowCreateModal] = useState(false);

  const onCreateButtonClicked = () => {
    setShowCreateModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateModal(false);
  };

  return (
    <>
      {showCreateModal && <CreateCustomerModal show={showCreateModal} handleClose={onCreateModalClosed} />}
      <AnalyticsHeader
        title="Customers"
        headerButton={<CreateCustomersButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : customers && customers.length > 0 ? (
              <TableList customers={customers} columns={columns} />
            ) : (
              <EmptyTableState message="No customers yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'Active',
    path: 'active',
    tabLink: '/customers/active',
    element: <CustomersTable query={{ isActive: true, limit: 10000 }} />
  },
  {
    title: 'Inactive',
    path: 'inactive',
    tabLink: '/customers/inactive',
    element: <CustomersTable query={{ isActive: false }} />
  }
];

const customerRoutes = [
  {
    path: '/:status/:id',
    element: <CustomerDetail />
  }
];

const Customers = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {customerRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Customers;

interface TableListProps {
  customers: Customer[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<Customer, any>[];
}

const TableList = ({ customers, columns }: TableListProps) => {
  const tableInstance = useReactTable({ data: customers!, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <td key={cell.id}>
                      <Link to={`./${row.original._id}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
